import { FunctionComponent, ChangeEvent, ReactNode } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import { connect } from 'react-redux';
import { toggleBankAccountListExpanded } from '../../actions/checkoutPaymentActions';
import { isVerifyingBankAccount, isAddingBankAccount } from '../../reducers/checkoutPaymentReducer';

import { PAYMENTS } from '../../constants/checkoutConstants';

// components
import CheckoutBankAccount, {
    ActionsType,
    CheckoutPaymentErrors,
    ValidationUpdate,
    ValidationError,
} from './CheckoutBankAccount';

import styles from '../creditCard/styles/CheckoutCreditCardList.scss';

import { CheckoutBankAccountList_viewer$data } from './__generated__/CheckoutBankAccountList_viewer.graphql';
import { CheckoutBankAccountList_buyer$data } from './__generated__/CheckoutBankAccountList_buyer.graphql';
import { CheckoutBankAccountList_transaction$data } from './__generated__/CheckoutBankAccountList_transaction.graphql';

export type OnSelectEventType = (arg1: ChangeEvent, arg2: string, arg3?: boolean) => void;

export type WalletPaymentMethodType = {
    paymentMethod: {
        token: string;
        __typename: string;
    };
};

type Props = {
    viewer: CheckoutBankAccountList_viewer$data;
    buyer: CheckoutBankAccountList_buyer$data;
    transaction: CheckoutBankAccountList_transaction$data;
    shippingAddressId: string;
    actions: ActionsType;
    onSelect: OnSelectEventType;
    paymentMethodToken: string;
    isAdding?: boolean;
    isVerifying?: boolean;
    verifyBankAccountToken: string;
    lastValidBankAccountToken: string;
    expandableBankAccountList: boolean;
    checkoutPaymentErrors?: CheckoutPaymentErrors;
    onValidationUpdate?: ValidationUpdate;
    onValidationError?: ValidationError;
    deselectBankAccounts: boolean;
    isBankAccountListExpanded: boolean;
    onToggleBankAccountListExpanded: () => void;
};

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType[number];
type BuyerWallet = NonNullable<CheckoutBankAccountList_buyer$data['wallet']>;
type PaymentMethods = ArrayElement<NonNullable<BuyerWallet['paymentMethods']>>;
type PaymentMethod = NonNullable<PaymentMethods>['paymentMethod'];

type ACHPaymentMethod = Extract<PaymentMethod, { __typename: 'ACH' }>;

function isACHPaymentMethod(paymentMethod: PaymentMethod): paymentMethod is ACHPaymentMethod {
    return !!paymentMethod && paymentMethod.__typename === 'ACH';
}

const CheckoutBankAccountList: FunctionComponent<Props> = ({
    viewer,
    buyer,
    transaction,
    actions,
    onSelect,
    paymentMethodToken,
    isAdding,
    isVerifying,
    verifyBankAccountToken,
    lastValidBankAccountToken,
    expandableBankAccountList,
    isBankAccountListExpanded,
    onToggleBankAccountListExpanded,
    deselectBankAccounts,
    checkoutPaymentErrors,
    onValidationUpdate,
    onValidationError,
}) => {
    const walletPaymentMethods = buyer?.wallet?.paymentMethods || [];
    const bankAccounts = walletPaymentMethods.filter(bankAccount => {
        const paymentMethod = bankAccount?.paymentMethod;
        return (
            paymentMethod &&
            isACHPaymentMethod(paymentMethod) &&
            paymentMethod.__typename === PAYMENTS.TYPE_BANK_ACCOUNT
        );
    });

    const bankAccountsToShow = expandableBankAccountList ? bankAccounts.slice(0, 1) : bankAccounts;
    const expandableBankAccounts =
        expandableBankAccountList && isBankAccountListExpanded ? bankAccounts.slice(1) : [];

    const getCheckoutBankAccount = (paymentMethod: PaymentMethod): ReactNode => {
        if (!isACHPaymentMethod(paymentMethod)) {
            return null;
        }
        const bankAccountToken = paymentMethod.token || '';
        const bankAccountProps = {
            viewer,
            buyer,
            actions,
            onSelect,
            onValidationUpdate,
            onValidationError,
            checkoutPaymentErrors,
        };

        const isSelected =
            ((bankAccountToken === lastValidBankAccountToken ||
                bankAccountToken === paymentMethodToken) &&
                !isAdding &&
                !isVerifying) ||
            (bankAccountToken === verifyBankAccountToken && isVerifying && !deselectBankAccounts);

        return (
            <CheckoutBankAccount
                key={bankAccountToken}
                {...bankAccountProps}
                isSelected={isSelected}
                bankAccountToken={bankAccountToken}
                transaction={transaction}
            />
        );
    };

    return (
        <div data-tn="bank-accounts-container" className="fs-block">
            {bankAccountsToShow.map(({ paymentMethod }: $TSFixMe) =>
                getCheckoutBankAccount(paymentMethod)
            )}
            {expandableBankAccounts.map(({ paymentMethod }: $TSFixMe) =>
                getCheckoutBankAccount(paymentMethod)
            )}
            {expandableBankAccountList && bankAccounts.length > 1 && (
                <div
                    className={styles.expandLink}
                    onClick={onToggleBankAccountListExpanded}
                    onKeyDown={onToggleBankAccountListExpanded}
                    role="link"
                    tabIndex={0}
                    data-tn={`${isBankAccountListExpanded ? 'hide' : 'view'}-all-saved-accounts`}
                >
                    {isBankAccountListExpanded ? (
                        <FormattedMessage
                            id="checkout.CheckoutBankAccountList.hideList"
                            defaultMessage="Hide all saved accounts"
                        />
                    ) : (
                        <FormattedMessage
                            id="checkout.CheckoutBankAccountList.showList"
                            defaultMessage="View all saved accounts"
                        />
                    )}
                </div>
            )}
        </div>
    );
};

type ConnectTransactionType = {
    paymentMethodToken: string;
    shippingAddressId: string;
};

type ConnectPaymentType = {
    verifyBankAccountToken: string;
    lastValidBankAccountToken: string;
    deselectBankAccounts: boolean;
    isBankAccountListExpanded: boolean;
    isAdding: boolean;
};

type ConnectType = {
    payment: ConnectPaymentType;
    transaction: ConnectTransactionType;
};

const ConnectedCheckoutBankAccountList = connect(
    ({ transaction, payment }: ConnectType) => {
        const isVerifying = isVerifyingBankAccount(payment);
        return {
            shippingAddressId: transaction.shippingAddressId,
            paymentMethodToken: transaction.paymentMethodToken,
            verifyBankAccountToken: payment.verifyBankAccountToken,
            lastValidBankAccountToken: payment.lastValidBankAccountToken,
            deselectBankAccounts: payment.deselectBankAccounts,
            isBankAccountListExpanded: payment.isBankAccountListExpanded,
            isAdding: isAddingBankAccount(payment),
            isVerifying,
        };
    },
    {
        onToggleBankAccountListExpanded: toggleBankAccountListExpanded,
    }
)(CheckoutBankAccountList);

export default createFragmentContainer(ConnectedCheckoutBankAccountList, {
    viewer: graphql`
        fragment CheckoutBankAccountList_viewer on Viewer {
            ...CheckoutBankAccount_viewer
        }
    `,
    buyer: graphql`
        fragment CheckoutBankAccountList_buyer on User {
            ...CheckoutBankAccount_buyer
            wallet {
                paymentMethods {
                    paymentMethod {
                        __typename
                        ... on ACH {
                            token
                            display
                        }
                    }
                    shippingAddresses {
                        serviceId
                    }
                }
            }
        }
    `,
    transaction: graphql`
        fragment CheckoutBankAccountList_transaction on Transaction {
            ...CheckoutBankAccount_transaction
        }
    `,
});
